// src/utils/errorLogger.js
import { trackEvent } from './analytics';
import { dbService } from '../firebaseInstance';
import moment from 'moment';
import { arrayUnion } from 'firebase/firestore';

/**
 * 표준 에러 로깅 유틸리티
 * - 콘솔에 구조화된 로그 출력
 * - GA4 이벤트 전송(trackEvent)
 * - 필요 시 Firestore 로깅 확장 가능
 */
let __trace = [];
let __session = { id: null, startedAt: null, context: null };

export const errorLogger = {
  startTrace: (context = {}) => {
    try {
      __session.id = `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
      __session.startedAt = new Date().toISOString();
      __session.context = context;
      __trace = [];
      try {
        localStorage.setItem('traceSession', JSON.stringify({ ...__session }));
      } catch (_) {}
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('[errorLogger] failed to start trace', e);
    }
  },
  trace: (step, data = {}) => {
    try {
      const entry = { step, at: new Date().toISOString(), ...data };
      __trace.push(entry);
      try {
        localStorage.setItem('traceLast', JSON.stringify(entry));
      } catch (_) {}
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('[errorLogger] failed to add trace', e);
    }
  },
  flushOnError: async (errorMeta = {}) => {
    try {
      const userID = localStorage.getItem('userID');
      if (!userID || !dbService) return;

      const dateKey = moment().format('YYYY-MM-DD');
      const sessionId = __session.id || `${Date.now()}`;

      const summaryRef = dbService
        .collection('lifetimeThreadErrors')
        .doc(userID)
        .collection('logs')
        .doc(dateKey);

      const sessionRef = dbService
        .collection('lifetimeThreadErrors')
        .doc(userID)
        .collection('sessions')
        .doc(sessionId);

      const summaryEntry = {
        timestamp: new Date().toISOString(),
        event: errorMeta.event || 'unknown',
        component: errorMeta.component || 'unknown',
        status: typeof errorMeta.status === 'number' ? errorMeta.status : null,
        endpoint: errorMeta.endpoint ?? null,
        since: errorMeta.since ?? null,
        until: errorMeta.until ?? null,
      };

      // 요약 append (원자적)
      await summaryRef.set({ entries: arrayUnion(summaryEntry) }, { merge: true });

      // 세션 상세 저장
      await sessionRef.set(
        {
          sessionId,
          startedAt: __session.startedAt,
          context: __session.context || null,
          error: { ...errorMeta, at: new Date().toISOString() },
          steps: __trace,
        },
        { merge: true }
      );
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('[errorLogger] failed to flush trace to Firestore', e);
    }
  },
  log: async (payload) => {
    try {
      const {
        component,
        event,
        endpoint,
        status,
        errorCode,
        tokenPresent,
        since,
        until,
        month,
        year,
        returnedCount,
        pagingNext,
        phase,
        stack,
        extra,
      } = payload || {};

      // Console structured log
      // eslint-disable-next-line no-console
      console.error('[DataLoadError]', {
        component,
        event,
        endpoint,
        status,
        errorCode,
        tokenPresent,
        since,
        until,
        month,
        year,
        returnedCount,
        pagingNext,
        phase,
        stack,
        extra,
      });

      // Analytics
      trackEvent('data_load_error', {
        component: component || 'unknown',
        event: event || 'unknown',
        endpoint: endpoint || 'n/a',
        status: typeof status === 'number' ? status : null,
        token_present: !!tokenPresent,
        since: since ?? null,
        until: until ?? null,
        month: month ?? null,
        year: year ?? null,
        returned_count: returnedCount ?? null,
        paging_next: !!pagingNext,
      });

      // Firestore 로깅 (user별 문서, 날짜별 lifetime threads 생성 실패 로그)
      try {
        const userID = localStorage.getItem('userID');
        if (userID && dbService) {
          const dateKey = moment().format('YYYY-MM-DD');
          const logRef = dbService
            .collection('lifetimeThreadErrors')
            .doc(userID)
            .collection('logs')
            .doc(dateKey);

          const logEntry = {
            timestamp: new Date().toISOString(),
            component: component || 'unknown',
            event: event || 'unknown',
            endpoint: endpoint || null,
            status: typeof status === 'number' ? status : null,
            errorCode: errorCode || null,
            tokenPresent: !!tokenPresent,
            since: since ?? null,
            until: until ?? null,
            month: month ?? null,
            year: year ?? null,
            returnedCount: returnedCount ?? null,
            pagingNext: !!pagingNext,
            phase: phase || null,
            stack: stack || null,
            extra: extra || null,
          };

          // 배열로 누적 (원자적)
          await logRef.set({ entries: arrayUnion(logEntry) }, { merge: true });
        }
      } catch (fsError) {
        // eslint-disable-next-line no-console
        console.warn('[errorLogger] failed to write Firestore log', fsError);
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('[errorLogger] failed to log error', e);
    }
  },
};

export default errorLogger;


